import type { Job, Pm, Profile, Stage, Task } from '@shared/index';

export type Table = 'jobs' | 'stages' | 'tasks' | 'pms';
export type ChangeEvent = 'INSERT' | 'UPDATE' | 'DELETE';
export type ChangeHandler = (table: Table, ev: ChangeEvent, row: Record<string, unknown> | null, old: Record<string, unknown> | null) => void;

export interface SyncRun {
  id: number;
  started_at: string;
  finished_at: string | null;
  trigger: 'cron' | 'webhook' | 'manual';
  status: 'running' | 'ok' | 'aborted' | 'failed';
  rows_read: number; added: number; updated: number; completed: number; deleted: number;
  errors: string[];
  details: Record<string, unknown>;
}

export interface UserRow extends Profile { created_at: string }

/** Thrown when the database refuses a write because the user can't edit. */
export class ReadOnlyError extends Error {
  constructor() { super('read-only'); }
}

/** Where the data lives: Supabase in real use, in-memory seed data for local demos. */
export interface Backend {
  kind: 'supabase' | 'memory';
  /** Signed-in email, or null. */
  session(): Promise<string | null>;
  onSignedIn(cb: () => void): void;
  sendMagicLink(email: string): Promise<void>;
  /** Sign in from an emailed link (?token_hash=…&type=…). Works in any browser, unlike a PKCE code link. */
  verifyLink(tokenHash: string, type: string): Promise<void>;
  /** Sign in with the 6-digit code from the email, typed into this tab. */
  verifyCode(email: string, code: string): Promise<void>;
  signOut(): Promise<void>;
  profile(): Promise<Profile | null>;

  loadAll(): Promise<{ jobs: Job[]; stages: Stage[]; tasks: Task[]; pms: Pm[] }>;
  /** Live changes. `onReconnect` fires after a dropped connection comes back, so the app can reload. */
  subscribe(onChange: ChangeHandler, onReconnect: () => void): void;

  updateJob(quote: string, patch: Record<string, unknown>): Promise<Job>;
  insertStage(row: Partial<Stage>): Promise<Stage>;
  updateStage(id: string, patch: Record<string, unknown>): Promise<Stage>;
  deleteStage(id: string): Promise<void>;
  insertTask(row: Partial<Task>): Promise<Task>;
  updateTask(id: string, patch: Record<string, unknown>): Promise<Task>;
  deleteTask(id: string): Promise<void>;

  listUsers(): Promise<UserRow[]>;
  updateUser(id: string, patch: { role?: string; pm_name?: string | null }): Promise<void>;
  listSyncRuns(limit: number): Promise<SyncRun[]>;
  syncNow(): Promise<{ ok: boolean; message: string }>;
}
